import { useCallback, useRef } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import { logger } from "@/lib/logger"
import {
  conversationsSessionStateSchema,
  normalizeSerializedCursors,
} from "@/utils/validation/externalBoundarySchemas"
import { warnOnInvalidExternalData } from "@/utils/validation/externalBoundaryValidation"

import type { ConversationsTableFilters } from "../types"

const log = logger("ConversationsSessionState")

const SESSION_KEY_BASE = "monitoring-session-state"
const SESSION_EXPIRY_MS = 30 * 60 * 1000 // 30 minutes

interface ConversationsSessionState {
  /** All page boundary cursors for reloading pages */
  loadedCursors: (string | undefined)[]
  /** Exact scroll position in pixels */
  scrollOffset: number
  /** Row that was clicked (for "you were here" highlight) */
  clickedRowId: string
  /** Timestamp for expiry check */
  timestamp: number
  /** Filter state at time of save */
  filters?: ConversationsTableFilters
  /** Selected row keys for restoring multi-select state */
  selectedKeys?: string[]
  /** Run IDs for selected simulations that may not be in the restored page cache */
  selectedRunIdsBySimulationId?: Record<string, string>
}

/**
 * Hook for saving and restoring uploaded conversation table state via sessionStorage.
 * Enables back-button restoration with scroll position and row highlight.
 */
export function useConversationsSessionState() {
  const highlightedRowRef = useRef<string | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  // Scoped by org + workspace so a workspace switch can't resurrect another
  // workspace's cursors/filters/selection via the sessionStorage back-button
  // restoration path. Kept in a ref (not state) since the callbacks below
  // fire imperatively, not from an effect.
  const sessionKey = useScopedStorageKey(SESSION_KEY_BASE)
  const sessionKeyRef = useRef(sessionKey)
  sessionKeyRef.current = sessionKey

  /**
   * Save state before navigating to detail page.
   * Called on row click before router.push.
   */
  const saveBeforeNavigation = useCallback(
    (params: {
      clickedRowId: string
      scrollOffset: number
      loadedCursors: (string | undefined)[]
      filters?: ConversationsSessionState["filters"]
      selectedKeys?: string[]
      selectedRunIdsBySimulationId?: Record<string, string>
    }) => {
      const state: ConversationsSessionState = {
        loadedCursors: params.loadedCursors,
        scrollOffset: params.scrollOffset,
        clickedRowId: params.clickedRowId,
        timestamp: Date.now(),
        filters: params.filters,
        selectedKeys: params.selectedKeys,
        selectedRunIdsBySimulationId: params.selectedRunIdsBySimulationId,
      }

      const key = sessionKeyRef.current
      if (!key) {
        return
      }

      try {
        sessionStorage.setItem(key, JSON.stringify(state))
      } catch (error) {
        log.error("Failed to save uploaded conversation session state", error)
      }
    },
    [],
  )

  /**
   * Check if there's valid saved state to restore.
   */
  const hasSavedState = useCallback((): boolean => {
    try {
      const key = sessionKeyRef.current
      const saved = key ? sessionStorage.getItem(key) : null
      if (!saved) {
        return false
      }

      const state = warnOnInvalidExternalData(
        JSON.parse(saved) as ConversationsSessionState,
        conversationsSessionStateSchema,
        {
          boundary: "Uploaded conversation session state",
          log,
        },
      )
      const isExpired = Date.now() - state.timestamp > SESSION_EXPIRY_MS

      return !isExpired
    } catch {
      return false
    }
  }, [])

  /**
   * Get saved state without consuming it.
   */
  const getSavedState = useCallback((): ConversationsSessionState | null => {
    try {
      const key = sessionKeyRef.current
      const saved = key ? sessionStorage.getItem(key) : null
      if (!saved) {
        return null
      }

      const state = warnOnInvalidExternalData(
        JSON.parse(saved) as ConversationsSessionState,
        conversationsSessionStateSchema,
        {
          boundary: "Uploaded conversation session state",
          log,
        },
      )
      const isExpired = Date.now() - state.timestamp > SESSION_EXPIRY_MS

      if (isExpired) {
        if (key) {
          sessionStorage.removeItem(key)
        }
        return null
      }

      return {
        ...state,
        loadedCursors: normalizeSerializedCursors(state.loadedCursors),
      }
    } catch {
      return null
    }
  }, [])

  /**
   * Restore scroll position and highlight row.
   * Call this after data is loaded and rendered.
   */
  const restoreScrollAndHighlight = useCallback(
    (
      scrollContainer: HTMLDivElement | null,
    ): {
      clickedRowId: string | null
      scrollOffset: number
    } | null => {
      const state = getSavedState()
      if (!state) {
        return null
      }

      // Clear saved state after consuming
      if (sessionKeyRef.current) {
        sessionStorage.removeItem(sessionKeyRef.current)
      }

      // Store for highlight
      highlightedRowRef.current = state.clickedRowId

      // Restore scroll position
      if (scrollContainer) {
        scrollContainerRef.current = scrollContainer
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          scrollContainer.scrollTo({
            top: state.scrollOffset,
            behavior: "instant",
          })
        })
      }

      return {
        clickedRowId: state.clickedRowId,
        scrollOffset: state.scrollOffset,
      }
    },
    [getSavedState],
  )

  /**
   * Get the row ID that should be highlighted (for "you were here").
   */
  const getHighlightedRowId = useCallback((): string | null => {
    return highlightedRowRef.current
  }, [])

  /**
   * Clear the highlight after a timeout.
   */
  const clearHighlightAfterDelay = useCallback((delayMs = 3000) => {
    setTimeout(() => {
      highlightedRowRef.current = null
    }, delayMs)
  }, [])

  /**
   * Clear all saved state.
   */
  const clearSavedState = useCallback(() => {
    try {
      if (sessionKeyRef.current) {
        sessionStorage.removeItem(sessionKeyRef.current)
      }
      highlightedRowRef.current = null
    } catch {
      // Ignore errors
    }
  }, [])

  return {
    saveBeforeNavigation,
    hasSavedState,
    getSavedState,
    restoreScrollAndHighlight,
    getHighlightedRowId,
    clearHighlightAfterDelay,
    clearSavedState,
  }
}
